import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavPillsIndicator } from 'hooks/useNavPillsIndicator';
import 'bootstrap-select';
import { useSelector } from 'react-redux';
import DashboardDataService from 'service/DashboardDataService';
import { DashboardConfigProjectResp } from 'domain/payload/response/dashboard/DashboardConfigProjectResp';
import { FormConfigListResp } from 'domain/payload/response/dashboard/FormConfigListResp';
import DashboardDetailModal from './DashboardDetailModal';
import DateRangeFilter from 'component/DateRangeFilterDropdown';
import ProjectFilterDropdown from 'component/ProjectFilterDropdown';
import SortFilterDropdown, { SortValue } from 'component/SortFilterDropdown';
import { formatElapsedTime } from 'helpers/FormatUtil';
import { TribeConfigListResp } from 'domain/payload/response/tribe/TribeResp';
import { Chart as ChartJS, ArcElement, Tooltip as ChartTooltip, Legend as ChartLegend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { DashboardChartResp } from 'domain/payload/response/dashboard/DashboardChartResp';
import CustomDropdown, { DropdownOption } from 'component/CustomDropdown';
import { PageSizeOption } from 'domain/constant/PageSizeOption';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { JobGroupConfigItemResp } from 'domain/payload/response/dashboard/JobGroupConfigResp';

ChartJS.register(ArcElement, ChartTooltip, ChartLegend);

// Color palette
const COLORS = {
  primary: '#3b82f6',      // Blue
  primaryLight: '#dbeafe', // Soft Blue
  success: '#10b981',      // Green
  successLight: '#d1fae5', // Soft Green
  warning: '#f59e0b',      // Orange/Yellow
  warningLight: '#fef3c7', // Soft Orange
  danger: '#ef4444',       // Red
  dangerLight: '#fee2e2',  // Soft Red
  info: '#06b6d4',         // Cyan
  purple: '#8b5cf6',       // Purple
  purpleLight: '#f3e8ff',  // Soft Purple
  grayText: '#64748b',     // Muted text
  darkText: '#1e293b',     // Dark slate text
  border: '#e2e8f0',       // Light border
  bgLight: '#f8fafc',      // Light gray slate
};

const Dashboard: React.FC = () => {

  const { profile } = useSelector((state: any) => state.auth);
  const [data, setData] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
  const [projectIdTerm, setProjectIdTerm] = useState('');
  const [formIdTerm, setFormIdTerm] = useState('');
  const [tribeIdTerm, setTribeIdTerm] = useState('');
  const [jobGroupIdTerm, setJobGroupIdTerm] = useState('');
  const [statusValueTerm, setStatusValueTerm] = useState<string | null>('Done');
  const [statusKeyTerm, setStatusKeyTerm] = useState<string | null>('3');
  const [notesFilter, setNotesFilter] = useState<string | null>('');
  const [timeRange, setTimeRange] = useState<{ startDate: string | null, endDate: string | null }>({ startDate: null, endDate: null });
  const [sortValue, setSortValue] = useState<SortValue>({
    sortBy: null,
    sortOrder: null
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any | null>(null);

  // LOADING
  const [loading, setLoading] = useState(false);
  const [loadingExportCsv, setLoadingExportCsv] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const [isLoadingSave, setIsLoadingSave] = useState(false);
  const [isSearchButtonDisabled, setIsSearchButtonDisabled] = useState(true);

  // CONFIG
  const [projectList, setProjectList] = useState<DashboardConfigProjectResp[]>([])
  const [formList, setFormList] = useState<FormConfigListResp[]>([])
  const [tribeList, setTribeList] = useState<TribeConfigListResp[]>([])
  const [jobGroupList, setJobGroupList] = useState<JobGroupConfigItemResp[]>([])

  const jobGroupOptions = useMemo(() => {
    return jobGroupList.map((item) => ({
      id: item.name,
      name: item.name,
      description: item.description ?? undefined,
    }));
  }, [jobGroupList]);

  // CHART
  const [chartData, setChartData] = useState<DashboardChartResp | null>(null);
  const [chartLoading, setChartLoading] = useState(false);

  // Animated nav-pills indicator
  const { navRef: statusNavRef, indicatorStyle: statusIndicatorStyle } = useNavPillsIndicator(statusKeyTerm);
  const { navRef: notesNavRef, indicatorStyle: notesIndicatorStyle } = useNavPillsIndicator(notesFilter);

  // Notification navigate
  const location = useLocation();
  const routerNavigate = useNavigate();
  const [isResolvingNotification, setIsResolvingNotification] = useState<boolean>(
    () => !!(location.state as any)?.payloadUrl
  );
  const [highlightResultId, setHighlightResultId] = useState<string | null>(null);
  const hasFetchStartedRef = useRef(false);

  const listAbortControllerRef = useRef<AbortController | null>(null);
  const chartAbortControllerRef = useRef<AbortController | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const isRunningParam = searchParams.get('isRunning');
  const isRunningFilter = useMemo(() => {
    if (isRunningParam === 'all') return null;
    if (isRunningParam === 'false') return false;
    return true;
  }, [isRunningParam]);
  const { navRef: runningNavRef, indicatorStyle: runningIndicatorStyle } = useNavPillsIndicator(isRunningFilter);

  const paletteColors = [COLORS.primary, COLORS.success, COLORS.warning, COLORS.purple, COLORS.info, COLORS.danger, '#ec4899', '#14b8a6', '#f97316', COLORS.grayText];
  const projectItems = chartData?.project ?? [];
  const statusItems = chartData?.status ?? [];
  const projectTotalCount = projectItems.reduce((acc, cur) => acc + cur.count, 0);
  const statusTotalCount = statusItems.reduce((acc, cur) => acc + cur.count, 0);
  const projectChartData = {
    labels: projectItems.map(d => d.name),
    datasets: [{
      data: projectItems.map(d => d.count),
      backgroundColor: projectItems.map((d, i) => d.color || paletteColors[i % paletteColors.length]),
      borderWidth: 0,
      cutout: '70%',
    }],
  };
  const statusChartData = {
    labels: statusItems.map(d => d.name),
    datasets: [{
      data: statusItems.map(d => d.count),
      backgroundColor: statusItems.map((d, i) => d.color || paletteColors[i % paletteColors.length]),
      borderWidth: 0,
      cutout: '70%',
    }],
  };
  const getOrCreateTooltip = (chart: any) => {
    let tooltipEl = document.getElementById('dashboard-chart-tooltip');

    if (!tooltipEl) {
      tooltipEl = document.createElement('div');
      tooltipEl.id = 'dashboard-chart-tooltip';
      tooltipEl.style.position = 'fixed';
      tooltipEl.style.pointerEvents = 'none';
      tooltipEl.style.zIndex = '9999';
      tooltipEl.style.background = '#1e293b';
      tooltipEl.style.color = 'white';
      tooltipEl.style.borderRadius = '8px';
      tooltipEl.style.padding = '8px 12px';
      tooltipEl.style.fontSize = '0.75rem';
      tooltipEl.style.whiteSpace = 'nowrap';
      tooltipEl.style.opacity = '0';
      tooltipEl.style.transition = 'opacity 0.1s ease';
      document.body.appendChild(tooltipEl);
    }

    return tooltipEl;
  };
  const externalTooltipHandler = (context: any) => {
    const { chart, tooltip } = context;
    const tooltipEl = getOrCreateTooltip(chart);

    if (tooltip.opacity === 0) {
      tooltipEl.style.opacity = '0';
      return;
    }

    if (tooltip.body) {
      const lines = tooltip.body.map((b: any) => b.lines).flat();
      const colors = tooltip.labelColors;

      const rows = lines.map((line: string, i: number) => {
        const bg = colors?.[i]?.backgroundColor || '#94a3b8';
        return `
          <div style="display:flex; align-items:center; gap:6px;">
            <span style="
              width:8px; height:8px; border-radius:50%;
              background:${bg}; flex-shrink:0; display:inline-block;
            "></span>
            <span>${line}</span>
          </div>
        `;
      });

      tooltipEl.innerHTML = rows.join('');
    }

    const canvasRect = chart.canvas.getBoundingClientRect();
    const tooltipRect = tooltipEl.getBoundingClientRect();

    let left = canvasRect.left + tooltip.caretX + 12;
    let top = canvasRect.top + tooltip.caretY - tooltipRect.height / 2;

    if (left + tooltipRect.width > window.innerWidth) {
      left = canvasRect.left + tooltip.caretX - tooltipRect.width - 12;
    }
    if (left < 0) left = 8;

    tooltipEl.style.left = left + 'px';
    tooltipEl.style.top = top + 'px';
    tooltipEl.style.opacity = '1';
  };
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: false,
        external: externalTooltipHandler,
        callbacks: {
          label: (context: any) => {
            const label = context.label || '';
            const value = context.raw || 0;
            const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
            return `${label}: ${value.toLocaleString('id-ID')} (${percentage}%)`;
          },
        },
      },
    },
  };
  const isAllDateSelected =
    (!timeRange.startDate || timeRange.startDate === "") &&
    (!timeRange.endDate || timeRange.endDate === "");

  const todayFormatted = new Date().toLocaleDateString('id-ID', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  const dashboardDataService = useMemo(() => DashboardDataService(), []);

  useEffect(() => {
    if (!searchParams.has('isRunning')) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('isRunning', 'true');
          return next;
        },
        { replace: true }
      );
    }
  }, []);

  const handleIsRunningFilter = (value: boolean | null) => {
    setProjectIdTerm('');
    setFormIdTerm('');
    setTribeIdTerm('');
    setJobGroupIdTerm('');
    setFormList([]);
    setTribeList([]);
    setJobGroupList([]);
    setCurrentPage(1);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value === null) {
        next.set('isRunning', 'all');
      } else {
        next.set('isRunning', String(value));
      }
      return next;
    }, { replace: true });
  };

  const handleProjectChange = (p: any) => {
    const newProjectId = p ? p.id : "";
    setProjectIdTerm(newProjectId);
    setFormIdTerm('');
    setTribeIdTerm('');
    setJobGroupIdTerm('');
    setJobGroupList([]);
    setCurrentPage(1);
  };

  useEffect(() => {
    if (projectIdTerm && projectIdTerm !== '') {
      fetchFormConfig();
      fetchTribeConfig();
      fetchJobGroupConfig();
    } else {
      setFormList([]);
      setTribeList([]);
      setJobGroupList([]);
    }
  }, [projectIdTerm]);

  const fetchFormConfig = async () => {
    const token = profile?.data.token;
    if (!token) {
      return;
    }

    try {
      const res = await dashboardDataService.dashboardDataFormConfig(token, projectIdTerm);
      setFormList(res.data ?? []);
    } catch (err: any) {
      console.error('Error fetching form config:', err);
      setFormList([]);
    }
  };

  const fetchTribeConfig = async () => {
    const token = profile?.data.token;
    if (!token) {
      return;
    }

    try {
      const res = await dashboardDataService.dashboardDataTribeConfig(token, projectIdTerm);
      setTribeList(res.data?.tribeList ?? []);
    } catch (err: any) {
      console.error('Error fetching tribe config:', err);
      setTribeList([]);
    }
  };

  const fetchJobGroupConfig = async () => {
    const token = profile?.data.token;
    if (!token) {
      return;
    }

    try {
      const res = await dashboardDataService.dashboardDataJobGroupConfig(token, projectIdTerm);
      setJobGroupList(res.data?.jobGroupList ?? res.data?.groupList ?? []);
    } catch (err: any) {
      console.error('Error fetching job group config:', err);
      setJobGroupList([]);
    }
  };

  const fetchData = async () => {
    const token = profile?.data.token;
    if (!token) return;

    if (listAbortControllerRef.current) {
      listAbortControllerRef.current.abort();
    }
    const controller = new AbortController();
    listAbortControllerRef.current = controller;

    setLoading(true);
    setIsSearchButtonDisabled(true);
    try {
      const res = await dashboardDataService.dashboardDataList(
        token,
        appliedSearchTerm,
        currentPage,
        pageSize,
        projectIdTerm != null && projectIdTerm !== "" ? projectIdTerm : null,
        formIdTerm != null && formIdTerm !== "" ? formIdTerm : null,
        tribeIdTerm != null && tribeIdTerm !== "" ? tribeIdTerm : null,
        statusKeyTerm != null && statusKeyTerm !== "" ? statusKeyTerm : null,
        timeRange.startDate != null && timeRange.startDate !== "" ? timeRange.startDate : null,
        timeRange.endDate != null && timeRange.endDate !== "" ? timeRange.endDate : null,
        sortValue.sortBy,
        sortValue.sortOrder,
        notesFilter,
        isRunningFilter,
        controller.signal,
        jobGroupIdTerm != null && jobGroupIdTerm !== "" ? jobGroupIdTerm : null
      );
      setData(res.data ?? []);
      setTotalPages(res.pagination?.totalPage ?? 0);
      setTotalRecords(res.pagination?.totalRecords ?? 0);
    } catch (err: any) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED' || err?.message === 'canceled') {
        return;
      }
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat mengambil data';
      window.alert(`Error: ${errorMessage}`);
    } finally {
      if (listAbortControllerRef.current === controller) {
        setLoading(false);
        setIsSearchButtonDisabled(false);
      }
    }
  };

  const fetchChartData = async () => {
    const token = profile?.data.token;
    if (!token) return;

    if (chartAbortControllerRef.current) {
      chartAbortControllerRef.current.abort();
    }
    const controller = new AbortController();
    chartAbortControllerRef.current = controller;

    setChartLoading(true);
    try {
      const res = await dashboardDataService.dashboardDataChart(
        token,
        appliedSearchTerm !== "" ? appliedSearchTerm : null,
        projectIdTerm !== "" ? projectIdTerm : null,
        formIdTerm !== "" ? formIdTerm : null,
        tribeIdTerm !== "" ? tribeIdTerm : null,
        statusKeyTerm !== "" ? statusKeyTerm : null,
        timeRange.startDate !== "" ? timeRange.startDate : null,
        timeRange.endDate !== "" ? timeRange.endDate : null,
        notesFilter,
        isRunningFilter,
        controller.signal,
        jobGroupIdTerm != null && jobGroupIdTerm !== "" ? jobGroupIdTerm : null
      );
      setChartData(res.data ?? null);
    } catch (err: any) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED' || err?.message === 'canceled') {
        return;
      }
      console.error('Error fetching chart data:', err);
      setChartData(null);
    } finally {
      if (chartAbortControllerRef.current === controller) {
        setChartLoading(false);
      }
    }
  };

  useEffect(() => {
    if (profile?.data.token) {
      fetchConfig();
    }
  }, [profile?.data.token]);

  useEffect(() => {
    return () => {
      listAbortControllerRef.current?.abort();
      chartAbortControllerRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    if (!profile?.data.token || isResolvingNotification) return;
    fetchData();
  }, [
    profile?.data.token,
    currentPage,
    pageSize,
    statusKeyTerm,
    tribeIdTerm,
    formIdTerm,
    jobGroupIdTerm,
    projectIdTerm,
    timeRange.startDate,
    timeRange.endDate,
    sortValue.sortBy,
    sortValue.sortOrder,
    notesFilter,
    appliedSearchTerm,
    isResolvingNotification,
    isRunningFilter,
  ]);

  useEffect(() => {
    if (!profile?.data.token || isResolvingNotification) return;
    const isOpeningFromNotification = Boolean(highlightResultId || (location.state as any)?.payloadUrl);
    if (!isOpeningFromNotification) {
      fetchChartData();
    }
  }, [
    profile?.data.token,
    statusKeyTerm,
    tribeIdTerm,
    formIdTerm,
    jobGroupIdTerm,
    projectIdTerm,
    timeRange.startDate,
    timeRange.endDate,
    notesFilter,
    appliedSearchTerm,
    isResolvingNotification,
    isRunningFilter,
  ]);

  const handleSearchClick = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleSearch();
  };

  const handleSearch = () => {
    if (loading) return;
    if (appliedSearchTerm === searchTerm && currentPage === 1) {
      fetchData();
      if (!highlightResultId) {
        fetchChartData();
      }
    } else {
      setAppliedSearchTerm(searchTerm);
      setCurrentPage(1);
    }
  };

  const handleExportToCsv = async () => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    if (!projectIdTerm) {
      window.alert('Silakan pilih project terlebih dahulu');
      return;
    }

    setLoadingExportCsv(true);
    try {
      const response = await dashboardDataService.dashboarDataExportToCsv(
        token,
        projectIdTerm,
        timeRange.startDate,
        timeRange.endDate,
        statusValueTerm!,
        searchTerm,
        formIdTerm !== "" ? formIdTerm : null,
        tribeIdTerm !== "" ? tribeIdTerm : null
      );

      window.alert(response?.message || 'File export sedang diproses dan akan dikirimkan ke email Anda.');
    } catch (error: any) {
      console.error("Error exporting CSV:", error);
      const errorMessage = error.response?.data?.message || error.message || error || 'Terjadi kesalahan saat export CSV';
      window.alert(`Error export CSV: ${errorMessage}`);
    } finally {
      setLoadingExportCsv(false);
    }
  };

  const fetchConfig = async () => {
    const token = profile?.data.token;
    if (!token) return;

    setIsLoadingConfig(true)
    try {
      const res = await dashboardDataService.dashboardDataConfig(token);
      setProjectList(res.data?.projectList ?? [])
      setIsLoadingConfig(false)
    } catch (err: any) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat mengambil konfigurasi';
      window.alert(`Error: ${errorMessage}`);
      setIsLoadingConfig(false)
    }
  };

  useEffect(() => {
    const payloadUrl = (location.state as any)?.payloadUrl as string | undefined;

    if (!payloadUrl) {
      return;
    }

    try {
      const queryString = payloadUrl.includes('?') ? payloadUrl.split('?')[1] : payloadUrl;
      const resultId = new URLSearchParams(queryString).get('search');

      if (resultId) {
        setSearchTerm(resultId);
        setAppliedSearchTerm(resultId);
        setCurrentPage(1);
        hasFetchStartedRef.current = false;
        setHighlightResultId(resultId);
      }
    } catch (err) {
      console.error('Gagal parsing payload.url dari notifikasi:', err);
    } finally {
      setIsResolvingNotification(false);
      routerNavigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
    }
  }, [location.key]);

  useEffect(() => {
    if (!highlightResultId) return;

    if (loading) {
      hasFetchStartedRef.current = true;
      return;
    }

    if (!hasFetchStartedRef.current) return;
    const match = data.find((item) => item.id === highlightResultId);
    if (match) {
      setSelectedItem(match);
      setShowModal(true);
    }

    setHighlightResultId(null);
    hasFetchStartedRef.current = false;
  }, [data, loading, highlightResultId]);

  return (
    <div className="page-wrapper">
      <div className="row">
        <div className="col-12">
          <div className="d-flex">
            <h5 style={{ flex: 1 }}>TransMore Dashboard</h5>
            <div>
              <p>TransMore Dashboard</p>
            </div>
          </div>
        </div>
        <div className="col-12">

          <div className="p-4" style={{ background: 'white' }}>
            <div className="mb-3">
              {/* Row 1: Running status pills, Project dropdown */}
              <div className="row g-2 align-items-stretch mb-2">
                <div className="col-12 col-md-auto" style={{ height: 50, maxHeight: 50 }}>
                  <div ref={runningNavRef} className="nav-pills-animated h-100" style={{ height: 50, maxHeight: 50 }}>
                    <div
                      className="nav-pill-indicator"
                      style={runningIndicatorStyle}
                    />
                    <button
                      type="button"
                      className={`h-100 nav-link ${isRunningFilter === null ? 'active' : ''}`}
                      onClick={() => handleIsRunningFilter(null)}
                    >
                      All
                    </button>
                    <button
                      type="button"
                      className={`h-100 nav-link ${isRunningFilter === true ? 'active' : ''}`}
                      onClick={() => handleIsRunningFilter(true)}
                    >
                      Running
                    </button>
                    <button
                      type="button"
                      className={`h-100 nav-link ${isRunningFilter === false ? 'active' : ''}`}
                      onClick={() => handleIsRunningFilter(false)}
                    >
                      Stopped
                    </button>
                  </div>
                </div>

                <div className="col-12 col-md-auto" style={{ width: 240, maxHeight: 50 }}>
                  <ProjectFilterDropdown
                    projects={projectList}
                    defaultSelectedId={projectIdTerm === "" ? null : projectIdTerm}
                    allowAllProject={true}
                    onChange={handleProjectChange}
                  />
                </div>
              </div>

              {/* Row 2: Date range, Form, Tribe, Job Group, Search, Export to CSV */}
              <div className="row g-2 align-items-stretch">
                <div className="col-12 col-md-auto" style={{ maxHeight: 50 }}>
                  <div className='w-100'>
                    <DateRangeFilter onChange={(v) => {
                      setTimeRange({ startDate: v.startDate, endDate: v.endDate })
                    }} />
                  </div>
                </div>

                {projectIdTerm && projectIdTerm !== "" && (
                  <div className="col-12 col-md-auto" style={{ height: 50, maxHeight: 50 }}>
                    <CustomDropdown
                      value={formIdTerm}
                      options={formList}
                      showDefaultOption={true}
                      defaultOptionLabel="All Form"
                      searchable={true}
                      searchPlaceholder="Search Form..."
                      onChange={(opt) => {
                        setFormIdTerm(opt.id != null ? String(opt.id) : '');
                        setCurrentPage(1);
                      }}
                      buttonStyle={{ width: 160 }}
                      dropdownStyle={{ minWidth: 200 }}
                    />
                  </div>
                )}

                {projectIdTerm && projectIdTerm !== "" && tribeList.length > 0 && (
                  <div className="col-12 col-md-auto" style={{ height: 50, maxHeight: 50 }}>
                    <CustomDropdown
                      value={tribeIdTerm}
                      options={tribeList}
                      showDefaultOption={true}
                      defaultOptionLabel="All Tribe"
                      searchable={true}
                      searchPlaceholder="Search Tribe..."
                      onChange={(opt) => {
                        setTribeIdTerm(opt.id != null ? String(opt.id) : '');
                        setCurrentPage(1);
                      }}
                      buttonStyle={{ width: 160 }}
                      dropdownStyle={{ minWidth: 200 }}
                    />
                  </div>
                )}

                {projectIdTerm && projectIdTerm !== "" && jobGroupList.length > 0 && (
                  <div className="col-12 col-md-auto" style={{ height: 50, maxHeight: 50 }}>
                    <CustomDropdown
                      value={jobGroupIdTerm}
                      options={jobGroupOptions}
                      showDefaultOption={true}
                      defaultOptionLabel="All Job Group"
                      searchable={true}
                      searchPlaceholder="Search Job Group..."
                      onChange={(opt) => {
                        setJobGroupIdTerm(opt.id != null ? String(opt.id) : '');
                        setCurrentPage(1);
                      }}
                      buttonStyle={{ width: 160 }}
                      dropdownStyle={{ minWidth: 200 }}
                    />
                  </div>
                )}

                <div className="col-12 col-sm col-md" style={{ height: 50, maxHeight: 50 }}>
                  <form className="h-100 d-flex" onSubmit={handleSearchClick}>
                    <input
                      type="text"
                      className="form-control form-control-compact me-2 h-100"
                      placeholder="Search..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <button type="submit" className="p-4 btn btn-primary btn-small-compact h-100" disabled={isSearchButtonDisabled}>
                      <i className="fa-solid fa-search"></i>
                    </button>
                  </form>
                </div>

                <div className="col-12 col-sm-auto col-md-auto" style={{ height: 50, maxHeight: 50 }}>
                  <button
                    className="h-100 btn btn-outline-primary w-100 d-flex justify-content-center align-items-center"
                    onClick={handleExportToCsv}
                    disabled={loadingExportCsv || projectIdTerm == ''}
                  >
                    {loadingExportCsv && (
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                    )}
                    Export To CSV
                  </button>
                </div>
              </div>
            </div>

            <div className="mb-3">
              {isAllDateSelected && (
                <div
                  className="mb-2"
                  style={{ fontSize: '0.75rem', color: COLORS.grayText }}
                >
                  Chart dibawah ini menampilkan data tanggal <strong>{todayFormatted}</strong>
                </div>
              )}

              <div className="row g-3">
                {[
                  { title: 'Project', items: projectItems, chartData: projectChartData, total: projectTotalCount },
                  { title: 'Status', items: statusItems, chartData: statusChartData, total: statusTotalCount },
                ].map((c, ci) => (
                  <div className="col-12 col-lg-6" key={ci}>
                    <div className="card border-0 shadow-sm p-4 h-100" style={{ borderRadius: '16px', background: '#ffffff', minHeight: '220px' }}>
                      <span className="text-muted text-uppercase fw-bold mb-3 d-block" style={{ fontSize: '0.7rem', letterSpacing: '0.75px' }}>
                        {c.title}
                      </span>

                      {chartLoading ? (
                        <div className="d-flex justify-content-center align-items-center flex-grow-1">
                          <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        </div>
                      ) : c.items.length === 0 ? (
                        <div className="d-flex justify-content-center align-items-center flex-grow-1 text-muted">
                          Tidak ada data
                        </div>
                      ) : (
                        <div className="row align-items-center w-100 g-3 flex-grow-1 m-0">
                          <div className="col-12 col-xl-4 d-flex justify-content-center justify-content-xl-start p-0">
                            <div style={{ width: '120px', height: '120px', position: 'relative', flexShrink: 0 }}>
                              <Doughnut data={c.chartData} options={chartOptions} />
                              <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                                <span className="text-muted" style={{ fontSize: '0.55rem', display: 'block', textTransform: 'uppercase' }}>Total</span>
                                <strong style={{ fontSize: '0.95rem', color: COLORS.darkText, lineHeight: 1 }}>
                                  {c.total.toLocaleString('id-ID')}
                                </strong>
                              </div>
                            </div>
                          </div>

                          <div className="col-12 col-xl-8 p-0 ps-xl-3 mt-3 mt-xl-0">
                            <div className="row g-2 m-0" style={{ fontSize: '0.72rem', maxHeight: '150px', overflowY: 'auto' }}>
                              {c.items.map((d, index) => (
                                <div key={index} className="col-6">
                                  <div className="d-flex align-items-center justify-content-between border-bottom pb-1">
                                    <div className="d-flex align-items-center gap-2 text-truncate" style={{ maxWidth: '60%' }}>
                                      <span style={{
                                        width: '8px', height: '8px', borderRadius: '50%',
                                        backgroundColor: d.color || paletteColors[index % paletteColors.length],
                                        display: 'inline-block', flexShrink: 0,
                                      }}></span>
                                      <span className="text-dark fw-medium text-truncate" title={d.name}>{d.name}</span>
                                    </div>
                                    <span className="text-muted text-nowrap ms-2" style={{ fontSize: '0.68rem' }}>
                                      {d.count.toLocaleString('id-ID')} ({d.percentage ?? 0}%)
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-3">
              <div className="d-flex justify-content-end align-items-center gap-3">
                <div className="overflow-auto" style={{ height: 50, maxHeight: 50 }}>
                  <div ref={statusNavRef} className="nav-pills-animated h-100" style={{ height: 50, maxHeight: 50 }}>
                    <div
                      className="nav-pill-indicator"
                      style={statusIndicatorStyle}
                    />
                    <button
                      type="button"
                      className={`nav-link ${statusKeyTerm === "" ? 'active' : ''}`}
                      onClick={() => {
                        setStatusKeyTerm("");
                        setStatusValueTerm("");
                        setCurrentPage(1);
                      }}
                    >
                      All
                    </button>
                    <button
                      type="button"
                      className={`nav-link ${statusKeyTerm === "3" ? 'active' : ''}`}
                      onClick={() => {
                        setStatusKeyTerm("3");
                        setStatusValueTerm("Done");
                        setCurrentPage(1);
                      }}
                    >
                      QC
                    </button>
                    <button
                      type="button"
                      className={`nav-link ${statusKeyTerm === "31" ? 'active' : ''}`}
                      onClick={() => {
                        setStatusKeyTerm("31");
                        setStatusValueTerm("Incomplete");
                        setCurrentPage(1);
                      }}
                    >
                      Incomplete
                    </button>
                    <button
                      type="button"
                      className={`nav-link ${statusKeyTerm === "5" ? 'active' : ''}`}
                      onClick={() => {
                        setStatusKeyTerm("5");
                        setStatusValueTerm("Rejected");
                        setCurrentPage(1);
                      }}
                    >
                      Rejected
                    </button>
                    <button
                      type="button"
                      className={`nav-link ${statusKeyTerm === "4" ? 'active' : ''}`}
                      onClick={() => {
                        setStatusKeyTerm("4");
                        setStatusValueTerm("Approved");
                        setCurrentPage(1);
                      }}
                    >
                      Approved
                    </button>
                    <button
                      type="button"
                      className={`nav-link ${statusKeyTerm === "8" ? 'active' : ''}`}
                      onClick={() => {
                        setStatusKeyTerm("8");
                        setStatusValueTerm("Archived");
                        setCurrentPage(1);
                      }}
                    >
                      Archived
                    </button>
                  </div>
                </div>

                <div className="overflow-auto" style={{ height: 50, maxHeight: 50 }}>
                  <div ref={notesNavRef} className="nav-pills-animated h-100" style={{ height: 50, maxHeight: 50 }}>
                    <div
                      className="nav-pill-indicator"
                      style={notesIndicatorStyle}
                    />
                    <button
                      type="button"
                      className={`nav-link ${notesFilter === "" ? 'active' : ''}`}
                      onClick={() => setNotesFilter("")}
                    >
                      Both
                    </button>
                    <button
                      type="button"
                      className={`nav-link ${notesFilter === "with_notes" ? 'active' : ''}`}
                      onClick={() => setNotesFilter("with_notes")}
                    >
                      With Notes
                    </button>
                    <button
                      type="button"
                      className={`nav-link ${notesFilter === "without_notes" ? 'active' : ''}`}
                      onClick={() => setNotesFilter("without_notes")}
                    >
                      Without Notes
                    </button>
                  </div>
                </div>

                <div style={{ height: 50, maxHeight: 50 }}>
                  <SortFilterDropdown
                    onChange={(value) => setSortValue(value)}
                  />
                </div>
              </div>
            </div>

            <table className="table table-condensed table-hover">
              <thead className="table-primary">
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Job Description</th>
                  <th>Installer</th>
                  <th>Project</th>
                  <th>Reference</th>
                  <th>Elapsed Time</th>
                  <th>Status</th>
                  <th>Notes</th>
                  <th>Submit At</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {
                  !loading ?
                    data.length > 0 ? (
                      data.map((item) => (
                        <tr key={item.id}>
                          <td>
                            {item.id}
                            {(item.hasSimilarity) && (
                              <i
                                className="fa-solid fa-circle-exclamation text-primary ms-2"
                                title="Nearby Store"
                                style={{ cursor: 'pointer' }}
                              />
                            )}
                            {(!item.retailPartnerRegistered) && (
                              <i
                                className="fa-solid fa-triangle-exclamation text-warning ms-2"
                                title="User Belum Registrasi"
                                style={{ cursor: 'pointer' }}
                              />
                            )}
                            {item.validationStatus && !item.validationStatus.isValid && (
                              <i
                                className="fa-solid fa-circle-exclamation text-danger ms-2"
                                title={item.validationStatus.errorMessage || `Validasi gagal: ${item.validationStatus.failedRules?.join(', ') || 'aturan tidak terpenuhi'}`}
                                style={{ cursor: 'pointer' }}
                              />
                            )}
                          </td>
                          <td>
                            <a
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                setSelectedItem(item);
                                setShowModal(true);
                              }}
                            >
                              {item.name}
                            </a>
                          </td>
                          <td>{item.jobDescription}</td>
                          <td>{item.installerName}</td>
                          <td>{item.projectName}</td>
                          <td>{item.placeReference}</td>
                          <td>{formatElapsedTime(item.elapsedTime)}</td>
                          <td><span className={`badge-status bg-${item.status.value?.toLowerCase()}`}>{item.status.value}</span></td>
                          <td>{item.notes || '-'}</td>
                          <td>{item.submitDate}</td>
                          <td>
                            <div className="d-flex">
                              <button
                                className="border-0 btn btn-outline-info btn-small-compact"
                                onClick={() => {
                                  setSelectedItem(item);
                                  setShowModal(true);
                                }}
                              >
                                <i className="fa-solid fa-eye"></i>
                              </button>
                              {/* {
                                item.status.key == 3 &&
                                <>
                                  <button className="border-0 btn btn-outline-danger btn-small-compact ms-2">
                                    <i className="fa-solid fa-times"></i>
                                  </button>
                                  <button className="border-0 btn btn-outline-success btn-small-compact ms-2">
                                    <i className="fa-solid fa-check"></i>
                                  </button>
                                </>
                              } */}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={11} className="text-center">
                          Tidak ada data
                        </td>
                      </tr>
                    )
                    :
                    <tr>
                      <td colSpan={11} className="text-center">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </td>
                    </tr>
                }
              </tbody>
            </table>
            <div className="mt-3 d-flex justify-content-end align-items-center">
              <div className="d-flex align-items-center">
                <div className="me-4 d-flex align-items-center">
                  <label className="me-2">Rows per page:</label>
                  <CustomDropdown
                    value={pageSize}
                    options={PageSizeOption.options}
                    onChange={(opt) => {
                      setPageSize(Number(opt.id));
                      setCurrentPage(1);
                    }}
                    align="top"
                  />
                </div>
                <span className="me-3">
                  {`${(currentPage - 1) * pageSize + 1}–${Math.min(currentPage * pageSize, totalRecords)} of ${totalRecords}`}
                </span>

                <nav>
                  <ul className="mb-0 pagination">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(1)} style={{ border: 0 }}>
                        <i className="fa fa-angle-double-left"></i>
                      </button>
                    </li>
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)} style={{ border: 0 }}>
                        <i className="fa fa-angle-left"></i>
                      </button>
                    </li>

                    <li className="page-item disabled">
                      <span className="page-link" style={{ border: 0 }}>{currentPage}</span>
                    </li>

                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)} style={{ border: 0 }}>
                        <i className="fa fa-angle-right"></i>
                      </button>
                    </li>
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(totalPages)} style={{ border: 0 }}>
                        <i className="fa fa-angle-double-right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showModal && selectedItem && (
        <DashboardDetailModal
          item={selectedItem}
          onClose={(decisionSuccess) => {
            setShowModal(false)
            if (decisionSuccess) {
              fetchData()
            }
          }}
        />
      )}

    </div>
  );

};

export default Dashboard;